import beTwMerge from '@/utils/beTwMerge';
import useStore from '@/store/useStore';
import { getAnchorId, getComponentMargin, isRichTextEmpty } from '@/utils/builderUtils';
import ResponsiveImage from '@/utils/ResponsiveImage';
import RichText from '@/components/RichText/RichText';
import { trackEventWithGaEvents } from '@/utils/gaDataPush';
import { GaEventsProps } from '@/store/types/IBuilder';
import { openDEChat } from '@/components/DigitalEngagementChat/DigitalEngagementChat';
import { headerCustomStyles, subHeadersStyles } from '@/consts/builder';
import { ITitle } from '../common/CommonFields';

type SectionHeader = {
  optionalLargeHeader: ITitle;
  optionalDescription: string;
};

type CustomerServiceTilesProps = {
  sectionHeader?: SectionHeader;
  headerPosition: 'left' | 'center';
  isAddOutline: boolean;
  isTwoIconOneLine: boolean;
  linkGroups?: {
    icon: {
      'Icon Alt Text'?: string;
      iconSrc: string;
    };
    label: {
      labelText: string;
      labelTag: 'h2' | 'h3';
    };
    linkDescription: string;
    isLinkGroupCtaAPopup?: boolean;
    isLinkedGroup: boolean;
    link?: string;
    gaEvents?: GaEventsProps[];
    onClick?: () => void;
  }[];
  margin: boolean;
  anchorLinkID: string;
};
export default function CustomerServiceTiles({
  sectionHeader,
  headerPosition,
  isAddOutline,
  isTwoIconOneLine,
  linkGroups = [],
  anchorLinkID,
  margin,
}: CustomerServiceTilesProps) {
  const isShowLinkGroups = linkGroups.length >= 2 && linkGroups.length <= 4;
  const setEmailUsModalVisible = useStore(state => state.setEmailUsModalVisible);
  const setIsPromoModalVisible = useStore(state => state.setIsPromoModalVisible);
  const HeaderTag = sectionHeader?.optionalLargeHeader?.titleHTMLTag || 'h2';

  const handleOnClickOption = option => {
    switch (option) {
      case 'openLivePerson':
        openDEChat();
        break;
      case 'openPromoModal':
        setIsPromoModalVisible(true);
        break;
      case 'openEmailUsModal':
        setEmailUsModalVisible(true);
        break;
      default:
        break;
    }
  };

  const renderItemHeader = linkGroup => {
    const SubHeaderTitle = linkGroup.label?.labelTag || 'h3';
    if (!linkGroup.label?.labelText) return null;
    const text = (
      <SubHeaderTitle
        className={beTwMerge(
          'tw-text-center tw-mt-0 tw-mb-2 md:tw-mb-4 tw-font-medium',
          isRichTextEmpty(linkGroup.linkDescription) && 'tw-mb-0 md:tw-mb-0',
          subHeadersStyles['h4']
        )}
      >
        {linkGroup.label?.labelText}
      </SubHeaderTitle>
    );
    switch (true) {
      case linkGroup.isLinkedGroup || !!linkGroup.link:
        return <div className="!tw-no-underline hover:tw-text-[#0d6363]">{text}</div>;
      default:
        return text;
    }
  };

  return (
    <div className={beTwMerge('tw-mx-4 lg:tw-mx-0', getComponentMargin(margin))} {...getAnchorId(anchorLinkID)}>
      <div className="tw-grid tw-col-span-full tw-grid-cols-2 md:tw-grid-cols-6 lg:tw-grid-cols-12 tw-gap-x-4 lg:tw-gap-x-6">
        {/* Section Header */}
        {(!!sectionHeader?.optionalLargeHeader?.content || !!sectionHeader?.optionalDescription) && (
          <div
            className={beTwMerge(
              'tw-col-span-full tw-text-center',
              !linkGroups.length ? 'tw-mb-0' : 'tw-mb-8',
              headerPosition === 'left' ? 'bs-tablet:tw-text-start' : ''
            )}
          >
            {!!sectionHeader?.optionalLargeHeader?.content && (
              <HeaderTag
                className={beTwMerge(headerCustomStyles[HeaderTag], sectionHeader?.optionalDescription && 'tw-mb-2')}
              >
                {sectionHeader?.optionalLargeHeader?.content}
              </HeaderTag>
            )}
            {!!sectionHeader?.optionalDescription && (
              <RichText className="tw-cursor-default" content={sectionHeader.optionalDescription} />
            )}
          </div>
        )}

        {/* Link Groups Section */}
        <div
          className={beTwMerge(
            'tw-col-span-full tw-grid tw-grid-cols-2 tw-grid-cols-6 lg:tw-grid-cols-12 md:tw-gap-x-6 tw-gap-y-6 lg:tw-gap-y-0',
            isTwoIconOneLine && 'tw-gap-x-4'
          )}
        >
          {isShowLinkGroups &&
            linkGroups.slice(0, 4).map((linkGroup, idx) => {
              const isLinked = linkGroup.isLinkedGroup || !!linkGroup.link;
              const onClick = () => {
                if (linkGroup.isLinkedGroup) {
                  linkGroup.gaEvents?.forEach(gaEvent => trackEventWithGaEvents(gaEvent, false));
                  handleOnClickOption(linkGroup.onClick);
                } else if (linkGroup.link) {
                  window.location.href = linkGroup.link;
                }
              };
              return (
                <div
                  className={beTwMerge(
                    'tw-cursor-default tw-col-span-full tw-flex tw-flex-col tw-items-center tw-px-4 tw-py-6 md:tw-py-4 desktop:tw-min-h-[240px]',
                    isTwoIconOneLine && 'tw-col-span-3',
                    linkGroups.length === 4 && 'large:tw-col-span-3',
                    linkGroups.length === 3 && 'large:tw-col-span-4',
                    linkGroups.length === 2 && 'large:tw-col-span-6',
                    idx === linkGroups.length - 1 && 'tw-mb-0',
                    isLinked && 'tw-cursor-pointer'
                  )}
                  key={`${linkGroup.label?.labelText?.replace(' ', '-')}-${idx}`}
                  onClick={onClick}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') handleOnClickOption(linkGroup.onClick);
                  }}
                  role="button"
                  style={{
                    border: isAddOutline ? '1px solid #D9D9D9' : '',
                  }}
                  tabIndex={0}
                >
                  {linkGroup.icon?.iconSrc && (
                    <ResponsiveImage
                      alt={linkGroup.icon?.['Icon Alt Text'] || ''}
                      className="tw-mb-4"
                      sources={[
                        {
                          src: linkGroup.icon?.iconSrc,
                          size: { width: 64, height: 64, aspectRatio: 1 / 1 },
                          breakpoint: 0,
                        },
                      ]}
                    />
                  )}
                  <div className="tw-grid tw-grid-cols-1 tw-items-center tw-content-center tw-jusitify-items-center">
                    <div className="tw-mb-0">
                      {renderItemHeader(linkGroup)}
                      {!isRichTextEmpty(linkGroup.linkDescription) && (
                        <div>
                          <RichText
                            className="desktop:[&_p]:tw-leading-[1.38] tw-text-be-sm desktop:tw-text-be-base tw-text-center"
                            content={linkGroup.linkDescription}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
